/**
 * Strict validators for everything the site accepts from the ANU Observer
 * (and for the roster the browser accepts from the site).
 *
 * Pure module: no Next/React/Node imports, no package imports — it runs in
 * `node --test` as-is and in the browser for the roster validator. Every
 * parser throws `LiveValidationError` on the first deviation; nothing is
 * coerced. The cryptographic checks (event hash, chain continuity, state
 * hash) are NOT here — they belong to the vendored engine (`verifyNextEvent`,
 * `hashValue`) and are applied by the poller after these structural checks.
 */

import { TASK_PHRASE_KEYS } from './task-phrase.ts';
import type {
  AgentCognition,
  AgentKind,
  CognitionHealth,
  LabAgent,
  LiveChainEntry,
  TaskPhrase,
  TaskPhraseKey,
} from './types.ts';

const TASK_PHRASE_KEY_SET: ReadonlySet<string> = new Set(TASK_PHRASE_KEYS);

export class LiveValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LiveValidationError';
  }
}

export const HASH_RE = /^[a-f0-9]{64}$/;
/** Run ids are `run-<32 hex>` today; keep room for a prefix change, never for path tricks. */
export const RUN_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
export const UNIVERSE_ID_RE = /^U[0-9]{4,8}$/;
const EVENT_TYPE_RE = /^[a-z]+(\.[a-z_]+)+$/;
const PHASE_RE = /^[a-z_]{1,32}$/;
const ID_RE = /^[\x21-\x7e]{1,128}$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

const EVENT_KEYS = new Set([
  'schemaVersion',
  'runId',
  'universeId',
  'seq',
  'eventId',
  'previousHash',
  'hash',
  'tick',
  'phase',
  'type',
  'data',
  'actorId',
  'targetId',
  'causationId',
]);

export const AGENT_KINDS: readonly AgentKind[] = [
  'research',
  'knowledge',
  'architecture',
  'security',
  'experiment',
  'infrastructure',
  'product',
  'data',
  'review',
];
const AGENT_KIND_SET = new Set<string>(AGENT_KINDS);
const AGENT_STATUS_SET = new Set(['active', 'idle', 'moving']);
const COGNITION_SET = new Set<string>(['llm', 'code-fallback', 'idle']);

/** Maximum events a single page may carry (observer MAX_EVENT_LIMIT). */
export const MAX_EVENT_PAGE = 1000;
/** Maximum epoch-chain entries retained from /api/live. */
export const MAX_CHAIN_ENTRIES = 64;
/** Maximum agents accepted in a roster (design: 10k agents is the engine's own limit). */
export const MAX_ROSTER = 10_000;

// ── Observer payload types ──────────────────────────────────────────────

export interface ObserverRunListItem {
  runId: string;
  experimentId: string | null;
  universeId: string | null;
  completed: boolean;
  ticks: number | null;
  events: number | null;
  summaryAvailable: boolean;
}

export interface ObserverRunsList {
  count: number;
  runs: ObserverRunListItem[];
}

/** RunManifest as published by the observer; `mode` is open (live is coming). */
export interface ObserverManifest {
  schemaVersion: number;
  experimentId: string;
  engineVersion: string;
  mode: string;
  policyId: string;
  taskGeneratorId: string;
  cognitionId?: string;
  runId: string;
  universeId: string;
  seed: string;
  configHash: string;
}

export interface ObserverRunSummary {
  ticks: number;
  events: number;
  finalEventHash: string;
  finalStateHash: string;
}

export interface ObserverRunDetail {
  runId: string;
  manifest: ObserverManifest;
  summary: ObserverRunSummary | null;
  attestation: { commitment: string | null } | null;
  attestationStatus: string | null;
}

/** Structural envelope of one ANU LabEvent (hash arithmetic is the engine's). */
export interface ObserverEvent {
  schemaVersion: number;
  runId: string;
  universeId: string;
  seq: number;
  eventId: string;
  previousHash: string;
  hash: string;
  tick: number;
  phase: string;
  type: string;
  data: Record<string, unknown>;
  actorId?: string;
  targetId?: string;
  causationId?: string;
}

export interface ObserverEventsPage {
  runId: string;
  after: number;
  limit: number;
  events: ObserverEvent[];
  nextAfter: number;
  hasMore: boolean;
}

export interface ObserverWorldStateLike {
  schemaVersion: number;
  runId: string;
  universeId: string;
  tick: number;
  started: boolean;
  completed: boolean;
  agents: Record<string, unknown>;
  links: Record<string, unknown>;
  tasks: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ObserverState {
  runId: string;
  tick: number;
  seq: number;
  eventHash: string;
  stateHash: string;
  state: ObserverWorldStateLike;
}

export interface ObserverHead {
  runId: string | null;
  seq: number;
  tick: number;
  completed: boolean;
  eventHash: string | null;
}

export interface ObserverLive {
  experimentId: string | null;
  universeId: string | null;
  currentRunId: string | null;
  epoch: number | null;
  head: { lastSeq: number; lastTick: number; completed: boolean } | null;
  chain: LiveChainEntry[];
  anchoredAt: string | null;
  boundary: boolean;
  cognitionHealth: CognitionHealth | null;
}

/** The parts of a `cognition.recorded` payload the site reads. */
export interface CognitionRecordData {
  cohort: string | null;
  provider: string;
  model: string;
  rejected: string | null;
  tier: string | null;
  actions: number;
  totalTokens: number | null;
  latencyMs: number | null;
  starved: boolean;
}

// ── Primitive guards ────────────────────────────────────────────────────

function fail(message: string): never {
  throw new LiveValidationError(message);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function record(value: unknown, name: string): Record<string, unknown> {
  if (!isRecord(value)) fail(`${name} must be a JSON object`);
  return value;
}

function str(value: unknown, name: string, re: RegExp = ID_RE): string {
  if (typeof value !== 'string' || !re.test(value)) fail(`${name} is not a valid string`);
  return value;
}

function optStr(value: unknown, name: string, re: RegExp = ID_RE): string | null {
  if (value === undefined || value === null) return null;
  return str(value, name, re);
}

function int(value: unknown, name: string, min = 0): number {
  if (!Number.isSafeInteger(value) || (value as number) < min) {
    fail(`${name} must be an integer >= ${min}`);
  }
  return value as number;
}

function optInt(value: unknown, name: string, min = 0): number | null {
  if (value === undefined || value === null) return null;
  return int(value, name, min);
}

function bool(value: unknown, name: string): boolean {
  if (typeof value !== 'boolean') fail(`${name} must be a boolean`);
  return value;
}

function optBool(value: unknown, name: string, fallback: boolean): boolean {
  if (value === undefined || value === null) return fallback;
  return bool(value, name);
}

function hash(value: unknown, name: string): string {
  if (typeof value !== 'string' || !HASH_RE.test(value)) {
    fail(`${name} must be a lowercase SHA-256 digest`);
  }
  return value;
}

function isoTime(value: unknown, name: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string' || !ISO_RE.test(value)) fail(`${name} must be an ISO-8601 UTC time`);
  return value;
}

// ── Observer parsers ────────────────────────────────────────────────────

export function parseRunsList(value: unknown): ObserverRunsList {
  const v = record(value, 'runs list');
  if (!Array.isArray(v.runs)) fail('runs must be an array');
  if (v.runs.length > 10_000) fail('runs list is unreasonably large');
  const runs = v.runs.map((item, index) => {
    const r = record(item, `runs[${index}]`);
    return {
      runId: str(r.runId, 'runId', RUN_ID_RE),
      experimentId: optStr(r.experimentId, 'experimentId'),
      universeId: optStr(r.universeId, 'universeId', UNIVERSE_ID_RE),
      completed: bool(r.completed, 'completed'),
      ticks: optInt(r.ticks, 'ticks'),
      events: optInt(r.events, 'events'),
      summaryAvailable: optBool(r.summaryAvailable, 'summaryAvailable', false),
    };
  });
  const count = v.count === undefined ? runs.length : int(v.count, 'count');
  if (count !== runs.length) fail('runs count does not match the list');
  return { count, runs };
}

export function parseManifest(value: unknown): ObserverManifest {
  const m = record(value, 'manifest');
  const cognitionId = optStr(m.cognitionId, 'cognitionId');
  return {
    schemaVersion: int(m.schemaVersion, 'manifest.schemaVersion', 1),
    experimentId: str(m.experimentId, 'manifest.experimentId'),
    engineVersion: str(m.engineVersion, 'manifest.engineVersion'),
    mode: str(m.mode, 'manifest.mode', /^[a-z]{1,32}$/),
    policyId: str(m.policyId, 'manifest.policyId'),
    taskGeneratorId: str(m.taskGeneratorId, 'manifest.taskGeneratorId'),
    ...(cognitionId === null ? {} : { cognitionId }),
    runId: str(m.runId, 'manifest.runId', RUN_ID_RE),
    universeId: str(m.universeId, 'manifest.universeId', UNIVERSE_ID_RE),
    seed: str(m.seed, 'manifest.seed', /^[\x20-\x7e]{1,256}$/),
    configHash: hash(m.configHash, 'manifest.configHash'),
  };
}

export function parseRunDetail(value: unknown, expectedRunId?: string): ObserverRunDetail {
  const v = record(value, 'run detail');
  const runId = str(v.runId, 'runId', RUN_ID_RE);
  if (expectedRunId !== undefined && runId !== expectedRunId) fail('run detail is for another run');
  const manifest = parseManifest(v.manifest);
  if (manifest.runId !== runId) fail('manifest.runId does not match the run');
  let summary: ObserverRunSummary | null = null;
  if (v.summary !== undefined && v.summary !== null) {
    const s = record(v.summary, 'summary');
    summary = {
      ticks: int(s.ticks, 'summary.ticks'),
      events: int(s.events, 'summary.events'),
      finalEventHash: hash(s.finalEventHash, 'summary.finalEventHash'),
      finalStateHash: hash(s.finalStateHash, 'summary.finalStateHash'),
    };
  }
  let attestation: { commitment: string | null } | null = null;
  if (v.attestation !== undefined && v.attestation !== null) {
    const a = record(v.attestation, 'attestation');
    attestation = { commitment: optStr(a.commitment, 'attestation.commitment', /^sha256:[a-f0-9]{64}$/) };
  }
  return {
    runId,
    manifest,
    summary,
    attestation,
    attestationStatus: optStr(v.attestationStatus, 'attestationStatus', /^[a-z_]{1,64}$/),
  };
}

/** Structural check of one event envelope; hash arithmetic is left to the engine. */
export function parseEventEnvelope(value: unknown, name = 'event'): ObserverEvent {
  const e = record(value, name);
  for (const key of Object.keys(e)) {
    if (!EVENT_KEYS.has(key)) fail(`${name} contains unknown field ${key}`);
  }
  const out: ObserverEvent = {
    schemaVersion: int(e.schemaVersion, `${name}.schemaVersion`, 1),
    runId: str(e.runId, `${name}.runId`, RUN_ID_RE),
    universeId: str(e.universeId, `${name}.universeId`, UNIVERSE_ID_RE),
    seq: int(e.seq, `${name}.seq`, 1),
    eventId: str(e.eventId, `${name}.eventId`),
    previousHash: hash(e.previousHash, `${name}.previousHash`),
    hash: hash(e.hash, `${name}.hash`),
    tick: int(e.tick, `${name}.tick`),
    phase: str(e.phase, `${name}.phase`, PHASE_RE),
    type: str(e.type, `${name}.type`, EVENT_TYPE_RE),
    data: record(e.data, `${name}.data`),
  };
  for (const key of ['actorId', 'targetId', 'causationId'] as const) {
    if (e[key] !== undefined) out[key] = str(e[key], `${name}.${key}`);
  }
  return out;
}

export interface EventsPageExpectation {
  runId: string;
  after: number;
  limit: number;
}

/**
 * A page is accepted only when it is exactly the page that was asked for:
 * same run, contiguous sequence numbers from `after + 1`, no more than
 * `limit` events, and a cursor that equals the last sequence delivered.
 */
export function parseEventsPage(value: unknown, expected: EventsPageExpectation): ObserverEventsPage {
  const v = record(value, 'events page');
  const runId = str(v.runId, 'runId', RUN_ID_RE);
  if (runId !== expected.runId) fail('events page is for another run');
  const after = int(v.after, 'after');
  if (after !== expected.after) fail('events page after-cursor does not match the request');
  const limit = int(v.limit, 'limit', 1);
  if (limit > MAX_EVENT_PAGE) fail('events page limit exceeds the observer maximum');
  if (!Array.isArray(v.events)) fail('events must be an array');
  if (v.events.length > Math.min(limit, expected.limit)) fail('events page carries more events than requested');
  const events = v.events.map((item, index) => {
    const e = parseEventEnvelope(item, `events[${index}]`);
    if (e.runId !== runId) fail(`events[${index}] belongs to another run`);
    if (e.seq !== after + index + 1) fail(`events[${index}] breaks sequence continuity`);
    return e;
  });
  const nextAfter = int(v.nextAfter, 'nextAfter');
  if (nextAfter !== after + events.length) fail('nextAfter does not match the delivered events');
  const hasMore = bool(v.hasMore, 'hasMore');
  if (hasMore && events.length === 0) fail('an empty page cannot claim more events');
  return { runId, after, limit, events, nextAfter, hasMore };
}

/**
 * Entries a single checkpoint map (agents/links/tasks) may carry. Far above
 * anything the engine produces, far below anything that could exhaust the
 * process before the body cap does.
 */
export const MAX_STATE_ENTRIES = 100_000;

/**
 * Walks one `Record<id, entry>` of a checkpoint and checks the VALUES, not
 * just that the container is an object: a `{"L1": null}` link map hashes to
 * a self-consistent stateHash but blows up the first fold that reads
 * `link.id`. Nothing is coerced or rewritten — the state object handed back
 * is byte-identical to the parsed one, so `hashValue(state)` still matches
 * the observer's `stateHash`.
 */
function stateEntries(
  value: unknown,
  name: string,
  check: (entry: Record<string, unknown>, key: string, where: string) => void,
): void {
  const map = record(value, name);
  const keys = Object.keys(map);
  if (keys.length > MAX_STATE_ENTRIES) fail(`${name} carries more than ${MAX_STATE_ENTRIES} entries`);
  for (const key of keys) {
    const where = `${name}[${key}]`;
    const entry = record(map[key], where);
    check(entry, key, where);
  }
}

/** Counter maps (`actionCounts`, `taskCounts`): keys are free, values must be numbers. */
function counterMap(value: unknown, name: string): void {
  if (value === undefined) return;
  const m = record(value, name);
  for (const [key, raw] of Object.entries(m)) {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) fail(`${name}.${key} must be a finite number`);
  }
}

export function parseStateResponse(value: unknown, expectedRunId: string): ObserverState {
  const v = record(value, 'state response');
  const runId = str(v.runId, 'runId', RUN_ID_RE);
  if (runId !== expectedRunId) fail('state response is for another run');
  const tick = int(v.tick, 'tick');
  const seq = int(v.seq, 'seq');
  const eventHash = hash(v.eventHash, 'eventHash');
  const stateHash = hash(v.stateHash, 'stateHash');
  const s = record(v.state, 'state');
  if (int(s.schemaVersion, 'state.schemaVersion', 1) !== 1) fail('state schemaVersion is not projectable');
  if (str(s.runId, 'state.runId', RUN_ID_RE) !== runId) fail('state.runId does not match the run');
  str(s.universeId, 'state.universeId', UNIVERSE_ID_RE);
  if (int(s.tick, 'state.tick') !== tick) fail('state.tick does not match the checkpoint tick');
  bool(s.started, 'state.started');
  bool(s.completed, 'state.completed');

  // Every field the site's fold dereferences is checked here; unknown extra
  // fields of an entry are left alone (the engine's reducer owns them).
  stateEntries(s.agents, 'state.agents', (a, key, where) => {
    if (str(a.id, `${where}.id`) !== key) fail(`${where}.id does not match its key`);
    bool(a.active, `${where}.active`);
    int(a.generation, `${where}.generation`);
    counterMap(a.actionCounts, `${where}.actionCounts`);
    counterMap(a.taskCounts, `${where}.taskCounts`);
    optInt(a.retiredTick, `${where}.retiredTick`);
  });
  stateEntries(s.links, 'state.links', (l, key, where) => {
    if (str(l.id, `${where}.id`) !== key) fail(`${where}.id does not match its key`);
    str(l.left, `${where}.left`);
    str(l.right, `${where}.right`);
    int(l.strengthPpm, `${where}.strengthPpm`);
    int(l.createdTick, `${where}.createdTick`);
    int(l.lastUsedTick, `${where}.lastUsedTick`);
  });
  stateEntries(s.tasks, 'state.tasks', (t, key, where) => {
    if (str(t.id, `${where}.id`) !== key) fail(`${where}.id does not match its key`);
    str(t.family, `${where}.family`);
    int(t.createdTick, `${where}.createdTick`);
    int(t.deadlineTick, `${where}.deadlineTick`);
    str(t.status, `${where}.status`, /^[a-z_]{1,32}$/);
    optStr(t.claimedBy, `${where}.claimedBy`);
  });

  if (seq === 0 && s.started === true) fail('a started world cannot sit at seq 0');
  return { runId, tick, seq, eventHash, stateHash, state: s as ObserverWorldStateLike };
}

export function parseHeadResponse(value: unknown, expectedRunId: string): ObserverHead {
  const v = record(value, 'head response');
  const runId = optStr(v.runId, 'runId', RUN_ID_RE);
  if (runId !== null && runId !== expectedRunId) fail('head response is for another run');
  const seq = int(v.lastSeq ?? v.seq, 'lastSeq');
  const tick = int(v.lastTick ?? v.tick, 'lastTick');
  const eventHashRaw = v.lastHash ?? v.eventHash;
  return {
    runId,
    seq,
    tick,
    completed: optBool(v.completed, 'completed', false),
    eventHash: eventHashRaw === undefined || eventHashRaw === null ? null : hash(eventHashRaw, 'lastHash'),
  };
}

export function parseCognitionHealth(value: unknown): CognitionHealth | null {
  if (value === undefined || value === null) return null;
  const h = record(value, 'cognitionHealth');
  // consulted/unavailable/starved are independent per-agent outcomes for the
  // trailing window (a successful answer, a provider failure, or an agent too
  // poor to try) — there is no ordering between them. A real provider outage
  // can legitimately produce 0 consulted and many unavailable in one window;
  // that is exactly what STALE(provider outage) exists to report honestly,
  // not a shape worth refusing.
  const consulted = int(h.consulted, 'cognitionHealth.consulted');
  const unavailable = int(h.unavailable, 'cognitionHealth.unavailable');
  const starved = int(h.starved, 'cognitionHealth.starved');
  return { consulted, unavailable, starved };
}

function scalarEntry(value: unknown, name: string): LiveChainEntry {
  const r = record(value, name);
  const out: LiveChainEntry = {};
  for (const [key, raw] of Object.entries(r)) {
    if (!/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(key)) continue;
    if (raw === null || typeof raw === 'boolean') out[key] = raw;
    else if (typeof raw === 'number' && Number.isFinite(raw)) out[key] = raw;
    else if (typeof raw === 'string' && raw.length <= 512) out[key] = raw;
    // nested values are dropped: the panel renders scalars only
  }
  return out;
}

export function parseLiveResponse(value: unknown): ObserverLive {
  const v = record(value, 'live response');
  let head: ObserverLive['head'] = null;
  if (v.head !== undefined && v.head !== null) {
    const h = record(v.head, 'head');
    head = {
      lastSeq: int(h.lastSeq, 'head.lastSeq'),
      lastTick: int(h.lastTick, 'head.lastTick'),
      completed: optBool(h.completed, 'head.completed', false),
    };
  }
  let chain: LiveChainEntry[] = [];
  if (v.chain !== undefined && v.chain !== null) {
    if (!Array.isArray(v.chain)) fail('chain must be an array');
    chain = v.chain.slice(-MAX_CHAIN_ENTRIES).map((item, index) => scalarEntry(item, `chain[${index}]`));
  }
  return {
    experimentId: optStr(v.experimentId, 'experimentId'),
    universeId: optStr(v.universeId, 'universeId', UNIVERSE_ID_RE),
    currentRunId: optStr(v.currentRunId, 'currentRunId', RUN_ID_RE),
    epoch: optInt(v.epoch, 'epoch'),
    head,
    chain,
    anchoredAt: isoTime(v.anchoredAt, 'anchoredAt'),
    boundary: optBool(v.boundary, 'boundary', false),
    cognitionHealth: parseCognitionHealth(v.cognitionHealth),
  };
}

/**
 * Lenient reader of a `cognition.recorded` payload: the record is evidence
 * written by the engine and already chain-verified, so an unexpected shape
 * is reported as "not a readable record" (null) rather than rejected.
 */
export const MAX_PROVIDER_CHARS = 64;
export const MAX_MODEL_CHARS = 128;

export function readCognitionRecord(data: Record<string, unknown>): CognitionRecordData | null {
  // Provider and model names are free text written by the engine and end up
  // in evidence rows, roster fields and the Ask Lab prompt: bound them here,
  // at the single place that reads them, rather than at every consumer.
  const provider = typeof data.provider === 'string' ? data.provider.slice(0, MAX_PROVIDER_CHARS) : null;
  const model = typeof data.model === 'string' ? data.model.slice(0, MAX_MODEL_CHARS) : null;
  if (provider === null || model === null) return null;
  const usage = isRecord(data.usage) ? data.usage : null;
  const totalTokens =
    usage !== null && Number.isSafeInteger(usage.totalTokens) ? (usage.totalTokens as number) : null;
  const rejected =
    typeof data.rejected === 'string' && data.rejected.length > 0 ? data.rejected.slice(0, 512) : null;
  return {
    cohort: typeof data.cohort === 'string' ? data.cohort : null,
    provider,
    model,
    rejected,
    tier: typeof data.tier === 'string' && data.tier.length <= 32 ? data.tier : null,
    actions: Array.isArray(data.actions) ? data.actions.length : 0,
    totalTokens,
    latencyMs: Number.isSafeInteger(data.latencyMs) ? (data.latencyMs as number) : null,
    starved: data.starved === true || provider === 'starved' || /\bstarved\b/i.test(rejected ?? ''),
  };
}

// ── Roster validator (browser side of the same contract) ────────────────

/** Interpolated values a phrase may carry: bounded scalars only, never objects. */
const MAX_PHRASE_PARAMS = 12;
const MAX_PHRASE_VALUE_CHARS = 128;

/**
 * Structured task phrase of one roster row (see `task-phrase.ts`). Anything
 * the renderer does not know — an unlisted key, a nested value, an oversize
 * string — is dropped rather than rejected: the row still carries its
 * pre-rendered English `task`, so the visitor loses a translation, never the
 * whole population. Only a phrase that is present and malformed in SHAPE
 * (not an object) is ignored the same way.
 */
export function readTaskPhrase(value: unknown): TaskPhrase | null {
  if (!isRecord(value)) return null;
  const k = value.k;
  if (typeof k !== 'string' || !TASK_PHRASE_KEY_SET.has(k)) return null;
  const raw = value.p;
  if (raw === undefined || raw === null) return { k: k as TaskPhraseKey };
  if (!isRecord(raw)) return { k: k as TaskPhraseKey };
  const p: Record<string, string | number | boolean> = {};
  let kept = 0;
  for (const [key, v] of Object.entries(raw)) {
    if (kept >= MAX_PHRASE_PARAMS) break;
    if (typeof v === 'string') p[key] = v.slice(0, MAX_PHRASE_VALUE_CHARS);
    else if (typeof v === 'number' && Number.isFinite(v)) p[key] = v;
    else if (typeof v === 'boolean') p[key] = v;
    else continue;
    kept += 1;
  }
  return { k: k as TaskPhraseKey, p };
}


/**
 * Validates a roster the browser receives from the site. Ids must be unique
 * printable strings, kinds and statuses must be from the closed unions,
 * activity must be a finite 0..1 number and every connection must point at
 * a roster member. Unknown extra fields are dropped.
 */
export function validateRoster(value: unknown): LabAgent[] {
  if (!Array.isArray(value)) fail('roster must be an array');
  if (value.length > MAX_ROSTER) fail('roster is unreasonably large');
  const ids = new Set<string>();
  const agents: LabAgent[] = value.map((item, index) => {
    const a = record(item, `roster[${index}]`);
    const id = str(a.id, `roster[${index}].id`);
    if (ids.has(id)) fail(`roster contains duplicate id ${id}`);
    ids.add(id);
    const kind = str(a.kind, `roster[${index}].kind`);
    if (!AGENT_KIND_SET.has(kind)) fail(`roster[${index}].kind is unknown`);
    const status = str(a.status, `roster[${index}].status`);
    if (!AGENT_STATUS_SET.has(status)) fail(`roster[${index}].status is unknown`);
    const activity = a.activity;
    if (typeof activity !== 'number' || !Number.isFinite(activity) || activity < 0 || activity > 1) {
      fail(`roster[${index}].activity must be within 0..1`);
    }
    if (!Array.isArray(a.connections) || a.connections.length > MAX_ROSTER) {
      fail(`roster[${index}].connections must be an array`);
    }
    const connections = a.connections.map((c) => str(c, `roster[${index}].connections[]`));
    const text = (field: string): string => {
      const raw = a[field];
      if (typeof raw !== 'string' || raw.length > 512) fail(`roster[${index}].${field} must be a short string`);
      return raw;
    };
    const out: LabAgent = {
      id,
      name: text('name'),
      kind: kind as AgentKind,
      role: text('role'),
      task: text('task'),
      status: status as LabAgent['status'],
      activity,
      connections,
    };
    if (typeof a.attachedTo === 'string') out.attachedTo = a.attachedTo.slice(0, 128);
    const phrase = readTaskPhrase(a.phrase);
    if (phrase !== null) out.phrase = phrase;
    if (a.cognition !== undefined) {
      const cognition = str(a.cognition, `roster[${index}].cognition`);
      if (!COGNITION_SET.has(cognition)) fail(`roster[${index}].cognition is unknown`);
      out.cognition = cognition as AgentCognition;
    }
    if (typeof a.tier === 'string' && a.tier.length <= 32) out.tier = a.tier;
    if (typeof a.model === 'string' && a.model.length <= 128) out.model = a.model;
    if (Number.isSafeInteger(a.generation)) out.generation = a.generation as number;
    if (Number.isSafeInteger(a.retiredTick)) out.retiredTick = a.retiredTick as number;
    return out;
  });
  for (const agent of agents) {
    for (const c of agent.connections) {
      if (!ids.has(c)) fail(`roster connection ${agent.id} -> ${c} points outside the roster`);
    }
  }
  return agents;
}
